import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Upload, Trash2, CheckCircle2, AlertCircle, ArrowLeft, ImageOff, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { getToken, clearToken } from '../lib/adminApi';
import { listUniversityLogos, uploadUniversityLogo, deleteUniversityLogo } from '../lib/uniLogosApi';
import { universities } from '../data/mock';

const BASE = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ has }) {
  return has ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
      <CheckCircle2 className="w-3 h-3" /> Uploaded
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-muted border border-border rounded-full px-2 py-0.5">
      <ImageOff className="w-3 h-3" /> Missing
    </span>
  );
}

export default function AdminUniversityLogos() {
  const nav = useNavigate();
  const [logos, setLogos] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState({});
  const [feedback, setFeedback] = useState(null); // { type: 'ok'|'err', text }

  const refresh = async () => {
    setLoading(true);
    try {
      const m = await listUniversityLogos();
      setLogos(m);
    } catch (err) {
      if (err.status === 401) { clearToken(); nav('/admin/login', { replace: true }); return; }
      setFeedback({ type: 'err', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) { nav('/admin/login', { replace: true }); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return universities.filter((u) => {
      const has = !!logos[u.slug];
      if (filter === 'has' && !has) return false;
      if (filter === 'missing' && has) return false;
      if (!s) return true;
      return (
        u.name.toLowerCase().includes(s) ||
        u.slug.toLowerCase().includes(s) ||
        (u.city || '').toLowerCase().includes(s)
      );
    });
  }, [q, filter, logos]);

  const stats = useMemo(() => ({
    total: universities.length,
    uploaded: Object.keys(logos).length,
    missing: universities.length - Object.keys(logos).length,
  }), [logos]);

  const onFile = async (slug, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setFeedback({ type: 'err', text: `File too large (${(file.size / 1024).toFixed(0)} KB). Max 2 MB.` });
      return;
    }
    setBusy((b) => ({ ...b, [slug]: 'up' }));
    setFeedback(null);
    try {
      const info = await uploadUniversityLogo(slug, file);
      setLogos((prev) => ({ ...prev, [slug]: { url: info.url, size: info.size, uploaded_at: info.uploaded_at } }));
      setFeedback({ type: 'ok', text: `Uploaded logo for ${slug}` });
    } catch (err) {
      if (err.status === 401) { clearToken(); nav('/admin/login', { replace: true }); return; }
      setFeedback({ type: 'err', text: `${slug}: ${err.message}` });
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[slug]; return c; });
    }
  };

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete logo for ${slug}?`)) return;
    setBusy((b) => ({ ...b, [slug]: 'del' }));
    try {
      await deleteUniversityLogo(slug);
      setLogos((prev) => { const c = { ...prev }; delete c[slug]; return c; });
      setFeedback({ type: 'ok', text: `Deleted logo for ${slug}` });
    } catch (err) {
      if (err.status === 401) { clearToken(); nav('/admin/login', { replace: true }); return; }
      setFeedback({ type: 'err', text: `${slug}: ${err.message}` });
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[slug]; return c; });
    }
  };

  return (
    <div className="bg-muted/40 min-h-[calc(100vh-80px)]" data-testid="admin-uni-logos">
      <SEO title="Admin — University Logos | Russian Education" description="Manage university logos" noindex />

      {/* Top bar */}
      <div className="bg-primary text-white">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-6 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand text-white flex items-center justify-center shadow-lg shadow-brand/40">
              <ImageIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/80 font-semibold">Admin</div>
              <h1 className="font-serif text-xl md:text-2xl font-bold">University Logos</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/20 border border-white/30 h-10 px-4 rounded-md">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 md:px-8 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-border p-5" data-testid="stats-total">
            <div className="text-xs uppercase tracking-widest text-brand font-bold">Total universities</div>
            <div className="mt-1 font-serif text-3xl font-extrabold text-primary">{stats.total}</div>
          </div>
          <div className="bg-white rounded-2xl border border-border p-5">
            <div className="text-xs uppercase tracking-widest text-brand font-bold">Logos uploaded</div>
            <div className="mt-1 font-serif text-3xl font-extrabold text-primary" data-testid="stats-uploaded">{stats.uploaded}</div>
          </div>
          <div className="bg-white rounded-2xl border border-border p-5" data-testid="stats-missing">
            <div className="text-xs uppercase tracking-widest text-brand font-bold">Missing</div>
            <div className="mt-1 font-serif text-3xl font-extrabold text-primary">{stats.missing}</div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-2xl border border-border p-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="uni-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, city or slug…"
              className="w-full h-11 pl-9 pr-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            />
          </div>
          {['all', 'missing', 'has'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`h-10 px-4 rounded-full text-sm font-semibold border transition-all
                ${filter === k
                  ? 'bg-brand text-white border-brand'
                  : 'bg-white text-primary border-border hover:border-brand/50 hover:text-brand'}`}
            >
              {k === 'all' ? 'All' : k === 'missing' ? 'Missing only' : 'With logo'}
            </button>
          ))}
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            className={`p-4 rounded-xl flex items-start gap-2 text-sm ${
              feedback.type === 'ok'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-brand/10 border border-brand/30 text-brand'
            }`}
            data-testid="uni-logos-feedback"
          >
            {feedback.type === 'ok' ? <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" /> : <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />}
            <span>{feedback.text}</span>
          </div>
        )}

        {/* Grid of universities */}
        {loading ? (
          <div className="p-16 text-center text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="uni-logo-grid">
            {filtered.map((u) => {
              const rec = logos[u.slug];
              const url = rec ? `${BASE}${rec.url}` : null;
              const b = busy[u.slug];
              return (
                <div key={u.slug} data-testid={`uni-row-${u.slug}`} className="bg-white rounded-2xl border border-border p-5 flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-lg bg-muted border border-border flex items-center justify-center overflow-hidden shrink-0">
                      {url ? (
                        <img src={url} alt="logo" className="w-full h-full object-contain" data-testid={`uni-logo-img-${u.slug}`} />
                      ) : (
                        <ImageOff className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif font-bold text-primary text-sm md:text-base leading-tight truncate">{u.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.city} · {u.slug}</div>
                      <div className="mt-1.5"><StatusBadge has={!!url} /></div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                      b === 'up' ? 'bg-brand/60 text-white' : 'bg-brand hover:bg-brand-dark text-white'
                    }`}>
                      <Upload className="w-3.5 h-3.5" />
                      {b === 'up' ? 'Uploading…' : (url ? 'Replace' : 'Upload')}
                      <input
                        data-testid={`uni-upload-${u.slug}`}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        className="hidden"
                        disabled={!!b}
                        onChange={(e) => onFile(u.slug, e.target.files?.[0])}
                      />
                    </label>
                    {url && (
                      <Button
                        data-testid={`uni-delete-${u.slug}`}
                        onClick={() => onDelete(u.slug)}
                        disabled={!!b}
                        variant="outline"
                        className="h-9 px-3 text-xs border-brand/40 text-brand hover:bg-brand hover:text-white"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center text-sm text-muted-foreground bg-white rounded-2xl border border-border">
            No universities match the current filters.
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center">
          PNG / JPG / WebP / SVG · max 2 MB per file · logos are served publicly at <code className="text-brand">/uni-logos/&lt;slug&gt;.&lt;ext&gt;</code>
        </p>
      </div>
    </div>
  );
}
